"use client";

import NextLink from "next/link";
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { TbWordmark } from "@/components/brand/tb-wordmark";
import {
  SITE_HEADER_FRAME_CLASS,
  SITE_HEADER_SURFACE_MOTION_CLASS,
  SITE_MENU_LOGO_WIDTH_CLASS,
} from "@/components/shared/site-header-frame";
import { SiteHeaderLogo } from "@/components/shared/site-header-logo";
import { Icon } from "@/components/ui/icon";
import { useHelpDialog } from "@/components/homepage/help-modal";
import { track } from "@/lib/analytics/data-layer";

const NAV_LINKS = [
  { hash: "#o-que-e", label: "O que é" },
  { hash: "#como", label: "Como funciona" },
  { hash: "#listas", label: "Listas de Sugestão" },
  { hash: "#ajuda", label: "Central de Informações" },
] as const;

const ACTION_LINKS = [
  {
    href: "/listas",
    itemId: "guest_find_list",
    journey: "guest",
    label: "Encontrar uma Lista",
  },
  {
    href: "/onboarding",
    itemId: "host_create_list",
    journey: "host",
    label: "Criar Minha Lista",
  },
  {
    href: "/anfitriao/dashboard",
    itemId: "host_open_list",
    journey: "host",
    label: "Acessar Minha Lista",
  },
] as const;

/**
 * One nav item, section link and action alike (`Catalogo.dc.html:109-115`): 12px uppercase at
 * `.16em`, weight 500, on a 44px target with 8px at each side.
 *
 * Two departures from that markup, both deliberate:
 *
 * - **The current item keeps this type.** The template sets `aria-current` links at 13px with no
 *   caps and no tracking (`:111`), which among uppercase neighbours reads as a defect rather than
 *   as a state. Here the current item differs by colour alone, and the state a reader needs is
 *   carried by `aria-current` either way.
 * - **`whitespace-nowrap`** is ours: the template never says it, and a label breaking inside a
 *   44px row is what it protects against.
 */
const NAV_LINK_CLASS =
  "inline-flex min-h-11 items-center justify-center whitespace-nowrap px-2 text-[12px] font-medium uppercase tracking-[0.16em] transition-colors hover:text-tb-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2";

/**
 * Where the horizontal nav takes over from the menu.
 *
 * The template says 1280px (`Catalogo.dc.html:1835`, `navDisplay: vw >= 1280`), and at 1280 —
 * at 1440 too — its own seven items do not fit: the Menu button lands at x1495, off a 1440 screen
 * (measured 2026-10-04, `desvios-aprovados.md:117`). The type is the design's; the threshold is
 * raised to the width where that type actually fits, which is the same mechanism the template
 * already uses, at an honest number. Below it the 44px Menu answers for the whole nav.
 */
const NAV_BREAKPOINT_CLASS = "min-[1600px]:flex";

const SCROLLED_THRESHOLD_PX = 60;

type HeaderSurface = "adaptive" | "solid";

interface PublicSiteHeaderProps {
  /**
   * The bag, on the pages that have one — the guest's list. Adopted from the checkout branch's
   * own header (`CartPageHeader`) on the person's ruling of 2026-09-24, so the guest can reopen
   * the drawer after adding, instead of only seeing it open once.
   */
  bag?: {
    isOpen: boolean;
    itemCount: number;
    onToggle: () => void;
    /** The cart could not be read: the icon says the count is unknown, not zero. */
    isError?: boolean;
  };
  activeAction?: "guest_find_list";
  /**
   * The homepage section this page belongs to: a house suggestion list sits under "Listas de
   * Sugestão", which the design's header (`hdrSug`) marks in green as the current page.
   */
  activeSection?: "#listas";
  homeHref: "#top" | "/";
  onScrollLockChange?: (locked: boolean) => void;
  /**
   * How a same-page anchor scrolls: the homepage hands in its Lenis-driven scroll (88px offset,
   * 1.2s out-cubic). Without it the browser's smooth scroll lands it, clear of the header through
   * `scroll-padding-top` (`globals.css`).
   */
  onSectionNavigate?: (target: HTMLElement) => void;
  sectionHrefPrefix: "" | "/";
  surface: HeaderSurface;
}

export function PublicSiteHeader({
  bag,
  activeAction,
  activeSection,
  homeHref,
  onScrollLockChange,
  onSectionNavigate,
  sectionHrefPrefix,
  surface,
}: PublicSiteHeaderProps) {
  const help = useHelpDialog();
  const [pastThreshold, setPastThreshold] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  /*
   * **On a public page the wordmark always goes home** — the person's ruling of 2026-10-06.
   *
   * The round trip between Meu Painel and the site belongs to the panel's own header
   * (`host-header.tsx`, `logoDestination`) and only there. Carrying it here sent a logged-in host
   * pressing the logo on Termos e Condições to `/anfitriao/dashboard`, where the panel's layout
   * intercepts an unfinished draft and lands them on `/onboarding` — a press on the logotype of a
   * legal page ending in a signup form (reported 2026-10-06).
   *
   * It also drops the `/api/auth/session` request these static pages made on every visit just to
   * decide this one link.
   */
  const logoHref = homeHref;

  useEffect(() => {
    if (surface === "solid") return;

    const onScroll = () =>
      setPastThreshold(window.scrollY > SCROLLED_THRESHOLD_PX);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [surface]);

  const handleMenuOpenChange = useCallback(
    (open: boolean) => {
      onScrollLockChange?.(open);
      setMenuOpen(open);
    },
    [onScrollLockChange],
  );

  const handleNavigation = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, closeDrawer = false) => {
      if (closeDrawer) handleMenuOpenChange(false);
      const href = event.currentTarget.getAttribute("href");
      if (!href?.startsWith("#")) return;

      const target = href.length > 1 ? document.querySelector<HTMLElement>(href) : null;
      if (!target) return;
      event.preventDefault();

      if (onSectionNavigate) {
        onSectionNavigate(target);
        return;
      }
      // Reduced motion jumps (the reference's `navScroll`); `scroll-padding-top` keeps the 88px.
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      target.scrollIntoView({ behavior: reduced ? "auto" : "smooth" });
    },
    [handleMenuOpenChange, onSectionNavigate],
  );

  const opaque = surface === "solid" || pastThreshold;
  const navColor = opaque
    ? "text-[var(--fg)]"
    : "text-[var(--tb-porcelain)]";
  const sectionHref = (hash: (typeof NAV_LINKS)[number]["hash"]) =>
    `${sectionHrefPrefix}${hash}`;
  const drawerItemClass =
    "grid grid-cols-[1fr_auto] items-center gap-x-4 border-b border-[rgba(61,63,57,0.15)] py-[26px] text-[var(--fg)] transition-colors hover:text-tb-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2";

  return (
    <Drawer open={menuOpen} onOpenChange={handleMenuOpenChange}>
      <header
        className={`tb-public-site-header fixed inset-x-0 top-0 z-[200] ${SITE_HEADER_FRAME_CLASS} transition-[background,border-color] ${SITE_HEADER_SURFACE_MOTION_CLASS}`}
        data-public-site-header=""
        data-surface={surface}
        style={{
          background: opaque
            ? "rgba(251,247,244,0.92)"
            : "rgba(251,247,244,0)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: `1px solid ${
            opaque ? "rgba(61,63,57,0.12)" : "rgba(61,63,57,0)"
          }`,
        }}
      >
        {/* The artwork at the panel's size — the person's rulings of 2026-09-20 for the whole
            product, over the Homepage source's lettering set in type and Busca's smaller ruler. */}
        <SiteHeaderLogo
          href={logoHref}
          destination="home"
          onClick={handleNavigation}
          className={navColor}
        />

        <nav
          aria-label="Navegação principal"
          className={`tb-public-site-header-nav hidden items-center ${NAV_BREAKPOINT_CLASS} ${navColor}`}
          style={{ gap: "clamp(2px, calc(100vw * 0.006), 10px)" }}
        >
          {NAV_LINKS.map((link) => (
            <NextLink
              key={link.hash}
              href={sectionHref(link.hash)}
              onClick={handleNavigation}
              aria-current={activeSection === link.hash ? "page" : undefined}
              className={`${NAV_LINK_CLASS} ${SITE_HEADER_SURFACE_MOTION_CLASS} ${
                activeSection === link.hash ? "text-tb-green" : "text-current"
              }`}
            >
              {link.label}
            </NextLink>
          ))}
          <span
            aria-hidden="true"
            className={`h-4 w-px transition-colors ${SITE_HEADER_SURFACE_MOTION_CLASS}`}
            style={{
              background: opaque
                ? "rgba(61,63,57,0.20)"
                : "rgba(245,237,230,0.34)",
            }}
          />
          {ACTION_LINKS.map((link) =>
            activeAction === link.itemId ? (
              <span
                aria-current="page"
                className={`${NAV_LINK_CLASS} text-tb-green`}
                key={link.itemId}
              >
                {link.label}
              </span>
            ) : (
              <NextLink
                key={link.itemId}
                href={link.href}
                onClick={() =>
                  track({
                    event: "select_content",
                    content_type: "cta",
                    item_id: link.itemId,
                    location: "header",
                    journey: link.journey,
                    link_url: link.href,
                  })
                }
                className={`${NAV_LINK_CLASS} text-current ${SITE_HEADER_SURFACE_MOTION_CLASS}`}
              >
                {link.label}
              </NextLink>
            ),
          )}
        </nav>

        {/* The bag, where the page has one: the drawer the guest can reopen after adding. */}
        {bag === undefined ? null : (
          <button
            type="button"
            aria-label={bag.isOpen ? "Fechar sacola" : "Abrir sacola"}
            onClick={bag.onToggle}
            className={`relative inline-flex size-11 flex-none cursor-pointer items-center justify-center border-none bg-transparent p-0 ${navColor} transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2`}
          >
            {bag.isOpen ? (
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            ) : (
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 7h12l1.2 13H4.8L6 7Z" />
                <path d="M9 7V5.5a3 3 0 0 1 6 0V7" />
              </svg>
            )}

            {!bag.isOpen && bag.itemCount > 0 ? (
              <span className="absolute right-0.5 top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-tb-green px-1 font-mono text-[10px] font-medium leading-none text-[var(--tb-porcelain)]">
                {bag.itemCount}
              </span>
            ) : !bag.isOpen && bag.isError === true ? (
              /* The count is not zero, it is unknown: without this the icon looks exactly like
                 an empty bag and whoever has gifts in theirs never opens it to find out. The
                 mark is decorative — the drawer is where the failure is stated and announced. */
              <span
                aria-hidden="true"
                className="absolute right-1.5 top-2 inline-flex size-1.5 rounded-full bg-tb-error"
              />
            ) : null}
          </button>
        )}

        <DrawerTrigger asChild>
          <button
            aria-expanded={menuOpen}
            aria-label="Menu"
            className={`tb-public-site-header-menu-trigger inline-flex size-11 flex-none flex-col items-center justify-center gap-[5px] border-none bg-transparent p-0 ${navColor} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2`}
            ref={menuTriggerRef}
            style={{ cursor: "pointer" }}
            type="button"
          >
            <span className="block h-[1.5px] w-[22px] bg-current" />
            <span className="block h-[1.5px] w-[22px] bg-current" />
            <span className="block h-[1.5px] w-[22px] bg-current" />
          </button>
        </DrawerTrigger>
      </header>

      <DrawerContent
        className="tb-editorial-body h-dvh max-h-dvh w-[min(440px,92vw)] max-w-none overflow-hidden"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          // See ui/drawer.tsx: without `preventScroll`, the browser may jump the page to reveal
          // the trigger as the lock releases.
          menuTriggerRef.current?.focus({ preventScroll: true });
        }}
        side="right"
      >
        <DrawerHeader className="gap-5 border-b-[rgba(61,63,57,0.15)] px-[clamp(22px,6vw,34px)] py-[18px]">
          <span className="flex min-w-0 flex-col gap-[5px]">
            {/* Still the drawer's heading: the wordmark's label is what names it. */}
            <DrawerTitle className="leading-none">
              <TbWordmark className={SITE_MENU_LOGO_WIDTH_CLASS} />
            </DrawerTitle>
            <span className="pl-[0.22em] text-[11px] uppercase tracking-[0.22em] text-[var(--fg-muted)]">
              Listas
            </span>
          </span>
          <DrawerDescription className="sr-only">
            Navegação pública de listas de presentes.
          </DrawerDescription>
          <DrawerClose className="grid size-11 flex-none place-items-center text-[var(--fg)] transition-opacity duration-300 hover:opacity-70">
            <Icon name="close" size={20} />
            <span className="sr-only">Fechar menu</span>
          </DrawerClose>
        </DrawerHeader>

        <DrawerBody
          className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain touch-pan-y p-0"
          data-public-site-drawer-scroll=""
        >
          <nav
            aria-label="Menu de listas"
            className="flex flex-none flex-col px-[clamp(22px,6vw,34px)]"
          >
          {activeAction === "guest_find_list" ? (
            <span
              aria-current="page"
              className={`${drawerItemClass} text-tb-green`}
            >
              <span className="col-span-2 text-[12px] uppercase tracking-[0.18em] text-[var(--fg-muted)]">
                Convidado
              </span>
              <span className="col-span-2 mt-[7px] font-display text-[clamp(20px,4.75vw,25px)] font-light uppercase leading-[1.2]">
                Presentear com Tania Bulhões
              </span>
              <span className="col-span-2 mt-2 text-[13px] leading-[1.55] text-[var(--fg-muted)]">
                Você está aqui: encontre a lista de quem você ama.
              </span>
            </span>
          ) : (
            <NextLink
              href="/listas"
              onClick={() => {
                handleMenuOpenChange(false);
                track({
                  event: "select_content",
                  content_type: "cta",
                  item_id: "guest_find_list",
                  location: "header_drawer",
                  journey: "guest",
                  link_url: "/listas",
                });
              }}
              className={drawerItemClass}
            >
              <span className="text-[12px] uppercase tracking-[0.18em] text-[var(--fg-muted)]">
                Convidado
              </span>
              <span className="row-span-3 text-[17px]">→</span>
              <span className="mt-[7px] font-display text-[clamp(20px,4.75vw,25px)] font-light uppercase leading-[1.2]">
                Presentear com Tania Bulhões
              </span>
              <span className="mt-2 text-[13px] leading-[1.55] text-[var(--fg-muted)]">
                Encontre a lista de quem você ama.
              </span>
            </NextLink>
          )}

          <NextLink
            href="/onboarding"
            onClick={() => {
              handleMenuOpenChange(false);
              track({
                event: "select_content",
                content_type: "cta",
                item_id: "host_create_list",
                location: "header_drawer",
                journey: "host",
                link_url: "/onboarding",
              });
            }}
            className={drawerItemClass}
          >
            <span className="text-[12px] uppercase tracking-[0.18em] text-[var(--fg-muted)]">
              Anfitrião
            </span>
            <span className="row-span-3 text-[17px]">→</span>
            <span className="mt-[7px] font-display text-[clamp(20px,4.75vw,25px)] font-light uppercase leading-[1.2]">
              Criar sua lista
            </span>
            <span className="mt-2 text-[13px] leading-[1.55] text-[var(--fg-muted)]">
              Já tem uma lista? Acesse com o seu CPF.
            </span>
          </NextLink>

          <NextLink
            href={sectionHref("#listas")}
            onClick={(event) => handleNavigation(event, true)}
            className={drawerItemClass}
          >
            <span className="text-[12px] uppercase tracking-[0.18em] text-[var(--fg-muted)]">
              Curadoria
            </span>
            <span className="row-span-3 text-[17px]">→</span>
            <span className="mt-[7px] font-display text-[clamp(20px,4.75vw,25px)] font-light uppercase leading-[1.2]">
              Curadoria de listas Tania Bulhões
            </span>
            <span className="mt-2 text-[13px] leading-[1.55] text-[var(--fg-muted)]">
              Composições prontas, assinadas pela maison.
            </span>
          </NextLink>

          {/*
            The fourth row of the reference approved on 2026-09-21 (`Homepage Lista.html`): the
            way in for whoever serves in the store, which the drawer had been missing. It is the
            only row that leaves the customer's product — hence its own journey in the event.
          */}
          <NextLink
            href="/admin/login"
            onClick={() => {
              handleMenuOpenChange(false);
              track({
                event: "select_content",
                content_type: "cta",
                item_id: "internal_area",
                location: "header_drawer",
                journey: "internal",
                link_url: "/admin/login",
              });
            }}
            className={drawerItemClass}
          >
            <span className="text-[12px] uppercase tracking-[0.18em] text-[var(--fg-muted)]">
              Consultor de vendas
            </span>
            <span className="row-span-3 text-[17px]">→</span>
            <span className="mt-[7px] font-display text-[clamp(20px,4.75vw,25px)] font-light uppercase leading-[1.2]">
              Entrar na área interna
            </span>
            <span className="mt-2 text-[13px] leading-[1.55] text-[var(--fg-muted)]">
              Acesso com e-mail e senha, para atender em loja.
            </span>
          </NextLink>
        </nav>

        <div className="flex flex-col gap-3.5 px-[clamp(22px,6vw,34px)] pb-[30px] pt-[26px]">
          {/* "Perguntas frequentes" left on 2026-09-29: the page does not exist in the MVP. */}
          <NextLink
            href={sectionHref("#ajuda")}
            onClick={(event) => {
              // The support channels open where the drawer is, as the reference does
              // (`openHelp`). Off the homepage there is no dialog around this header, and the
              // link keeps taking the person to the section.
              if (help !== null) {
                event.preventDefault();
                handleMenuOpenChange(false);
                help.open("header_drawer");
                return;
              }

              handleNavigation(event, true);
            }}
            className="block py-[11px] text-[13px] text-[var(--fg-muted)] transition-colors hover:text-tb-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2 [@media(min-width:900px)]:py-0"
          >
            Precisa de ajuda
          </NextLink>
        </div>

        <NextLink
          href={sectionHref("#ajuda")}
          onClick={(event) => handleNavigation(event, true)}
          className="mt-auto flex flex-col gap-[9px] border-t border-[rgba(61,63,57,0.15)] bg-[rgba(251,247,244,1)] p-[clamp(26px,6vw,34px)] text-[var(--fg)] transition-colors hover:bg-[var(--tb-porcelain)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2"
        >
          {/* Canon MOL-10: eyebrow 15px/500, title 23px Intrinseca 300 in uppercase. */}
          <span className="text-[15px] font-medium uppercase tracking-[0.16em] text-[var(--fg)]">
            Apoio
          </span>
          <span className="font-display text-[23px] font-light uppercase leading-[1.15]">
            Atendimento Concierge
          </span>
          <span className="text-[13px] leading-[1.55] text-[var(--fg-muted)]">
            Uma consultora acompanha você, da escolha ao embrulho.
          </span>
          <span className="mt-1.5 inline-flex items-center gap-2.5 text-[12px] uppercase tracking-[0.16em]">
            Falar com a concierge <span className="text-[15px]">→</span>
          </span>
          </NextLink>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
